/**
 * Wort-Diff fuer die Vorher/Nachher-Darstellung optimierter Produkttexte.
 *
 * Bewusst kein Fremdpaket: die Eingaben sind Produkttitel und
 * Beschreibungen, also einige hundert Tokens, und ein eigenes Modul ist
 * testbarer als eine Abhaengigkeit.
 *
 * Whitespace bleibt am jeweiligen Token haengen, damit sich beide
 * Ausgangstexte aus dem Ergebnis verlustfrei rekonstruieren lassen. Genau
 * das prueft der Roundtrip-Test.
 */

export type DiffOp = 'equal' | 'removed' | 'added';

export interface DiffToken {
  op: DiffOp;
  text: string;
}

/** Zerlegt in Tokens, die ihren nachfolgenden Whitespace mitfuehren. */
function tokenize(text: string): string[] {
  if (text === '') return [];
  return text.match(/\S+\s*/g) ?? [];
}

/** Vergleichsschluessel ohne Whitespace, damit "Wort " und "Wort" gleich sind. */
function key(token: string): string {
  return token.trim();
}

export function diffWords(before: string, after: string): DiffToken[] {
  const a = tokenize(before);
  const b = tokenize(after);

  // LCS-Laengenmatrix. (a.length+1) x (b.length+1).
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0),
  );

  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i][j] = key(a[i]) === key(b[j])
        ? lcs[i + 1][j + 1] + 1
        : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const out: DiffToken[] = [];
  const push = (op: DiffOp, text: string) => {
    const last = out[out.length - 1];
    if (last && last.op === op) last.text += text;
    else out.push({ op, text });
  };

  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (key(a[i]) === key(b[j])) {
      // equal entsteht nur bei exaktem Token-Match (inklusive Whitespace).
      // Damit bleiben beide Ausgangstexte aus dem Ergebnis verlustfrei rekonstruierbar.
      if (a[i] === b[j]) {
        push('equal', b[j]);
        i++;
        j++;
      } else {
        // Whitespace-Divergenz: Wort gleich, aber Spacing unterschiedlich.
        // Push removed + added getrennt, damit jede Version ihr Original-Whitespace behält.
        push('removed', a[i]);
        push('added', b[j]);
        i++;
        j++;
      }
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      push('removed', a[i]);
      i++;
    } else {
      push('added', b[j]);
      j++;
    }
  }
  while (i < a.length) push('removed', a[i++]);
  while (j < b.length) push('added', b[j++]);

  return out;
}
